import React from 'react';
import {
  Benefit,
  COMMON_PLUS_BENEFITS,
  KAEUFER_PLUS_BENEFITS,
  MIETER_PLUS_BENEFITS,
  WOHNEN_PLUS_BENEFITS,
} from '../@types/benefits';
import { useGetNotifications } from '../apiHooks/notifications.hook';
import { useGetBenefitsListPage } from './useGetBenefitsListPage.hook';
import { useUserInteractions } from './userInteractions';
import { useGetHasEntitlement } from './useGetHasEntitlement.hook';
import { EntitlementType } from '../@types/entitlement';
import { useGetCanVisitDeposit } from './useGetCanVisitDeposit.hook';
import { useGetHasProduct } from './useGetHasProduct.hook';
import { ProductType } from '../@types/product';
import { BenefitsListPage } from '../@types/benefitsListPage';
import { useIsTenantProtectionAvailable } from './useIsTenantProtectionAvailable';
import { useInsurancePackageStatus } from './useInsurancePackageStatus';

export const useGetActiveAnnotations = () => {
  const benefitsListPage = useGetBenefitsListPage();
  const { notifications } = useGetNotifications();
  const { viewedBenefits, isUserInteractionError } = useUserInteractions();
  const hasMieterPlus = useGetHasProduct(ProductType.MieterPlus);
  const hasKaeuferPlus = useGetHasProduct(ProductType.KaeuferPlus);

  const canVisitDeposit = useGetCanVisitDeposit();
  const isEntitledForTenantProtection = useIsTenantProtectionAvailable();
  const isEntitledForRelocationBox = useGetHasEntitlement(EntitlementType.RELOCATION_BOX);
  const { isRechtsberatungEntitled, isMietrechtsschutzEntitled } = useInsurancePackageStatus();

  const benefitVisibilityConditions = React.useMemo(
    () => ({
      [Benefit.DEPOSIT]: () => hasMieterPlus && canVisitDeposit,
      [Benefit.TENANT_PROTECTION_SERVICE]: () => isEntitledForTenantProtection,
      [Benefit.RELOCATION_BOX]: () => isEntitledForRelocationBox,
      [Benefit.MEDIATION]: () => isRechtsberatungEntitled,
      [Benefit.SCHLUESSELDIENST]: () => isRechtsberatungEntitled,
      [Benefit.RECHTSBERATUNG]: () => isRechtsberatungEntitled,
      [Benefit.MIETRECHTSSCHUTZ]: () => isMietrechtsschutzEntitled,
      [Benefit.FIRST_IN_INBOX]: () => hasKaeuferPlus && !viewedBenefits?.includes(Benefit.FIRST_IN_INBOX),
      [Benefit.EXCLUSIVE_LISTINGS]: () => hasKaeuferPlus && !viewedBenefits?.includes(Benefit.EXCLUSIVE_LISTINGS),
      [Benefit.OPPORTUNITY_CHECK]: () => hasKaeuferPlus && !viewedBenefits?.includes(Benefit.OPPORTUNITY_CHECK),
    }),
    [
      hasMieterPlus,
      canVisitDeposit,
      isEntitledForTenantProtection,
      isEntitledForRelocationBox,
      isRechtsberatungEntitled,
      isMietrechtsschutzEntitled,
      hasKaeuferPlus,
      viewedBenefits,
    ],
  );

  const filterBenefits = React.useCallback(
    (benefits: Benefit[], isKaeuferPlus = false) =>
      benefits.filter((benefit) => {
        if (notifications?.[benefit as keyof typeof notifications]?.marked) {
          return benefitVisibilityConditions[benefit as keyof typeof benefitVisibilityConditions]
            ? benefitVisibilityConditions[benefit as keyof typeof benefitVisibilityConditions]()
            : true;
        }
        if (
          benefit === Benefit.FIRST_IN_INBOX ||
          benefit === Benefit.EXCLUSIVE_LISTINGS ||
          benefit === Benefit.OPPORTUNITY_CHECK
        )
          return hasKaeuferPlus && !viewedBenefits?.includes(benefit);
        return !COMMON_PLUS_BENEFITS.includes(benefit) && isKaeuferPlus && !viewedBenefits?.includes(benefit);
      }),
    [notifications, viewedBenefits, benefitVisibilityConditions],
  );

  const mieterPlusActiveAnnotations = React.useMemo(
    () => filterBenefits([...COMMON_PLUS_BENEFITS, ...MIETER_PLUS_BENEFITS]),
    [filterBenefits],
  );

  const wohnenPlusActiveAnnotations = React.useMemo(
    () => filterBenefits([...COMMON_PLUS_BENEFITS, ...WOHNEN_PLUS_BENEFITS]),
    [filterBenefits],
  );

  const kauferPlusActiveAnnotations = React.useMemo(
    () => filterBenefits([...COMMON_PLUS_BENEFITS, ...KAEUFER_PLUS_BENEFITS], true),
    [filterBenefits],
  );

  const activeAnnotationCountByPage = React.useMemo(
    () => ({
      [BenefitsListPage.MieterPlus]: mieterPlusActiveAnnotations,
      [BenefitsListPage.WohnenPlus]: wohnenPlusActiveAnnotations,
      [BenefitsListPage.KauferPlus]: isUserInteractionError ? [] : kauferPlusActiveAnnotations,
    }),
    [mieterPlusActiveAnnotations, wohnenPlusActiveAnnotations, kauferPlusActiveAnnotations, isUserInteractionError],
  );

  return {
    activeAnnotations: activeAnnotationCountByPage[benefitsListPage],
    activeAnnotationsCount: activeAnnotationCountByPage[benefitsListPage]?.length || 0,
  };
};
